/**
 * Database, Table, and Field metadata schemas
 * Consolidated from useCardData.ts, useMbqlQuery.ts, useGetTables.ts
 */

import { z } from 'zod';

// ============================================================================
// Field Metadata
// ============================================================================

/**
 * Field metadata schema - describes columns in query results and database tables
 * Used across result_metadata, query_metadata, and table metadata endpoints
 */
export const fieldMetadataSchema = z.object({
  id: z.number().nullable().optional(),
  name: z.string(),
  display_name: z.string(),
  base_type: z.string().optional(),
  effective_type: z.string().optional(),
  semantic_type: z.string().nullable().optional(),
  database_type: z.string().optional(),
  table_id: z.number().nullable().optional(),
  field_ref: z.array(z.unknown()).optional(),
  source: z.string().optional(),
  unit: z.string().optional(),
  has_field_values: z.string().optional(),
  fk_target_field_id: z.number().nullable().optional(),
  dimensions: z.array(z.unknown()).optional(),
  fingerprint: z
    .object({
      global: z
        .object({
          'distinct-count': z.number().optional(),
          'nil%': z.number().nullable().optional(),
        })
        .optional(),
      type: z.record(z.unknown()).optional(),
    })
    .nullable()
    .optional(),
});

export type FieldMetadata = z.infer<typeof fieldMetadataSchema>;

// ============================================================================
// Table Metadata
// ============================================================================

/**
 * Table schema - database table metadata
 * Used by table endpoints and database metadata
 */
export const tableSchema = z.object({
  id: z.number(),
  name: z.string(),
  display_name: z.string(),
  db_id: z.number(),
  schema: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  entity_type: z.string().nullable().optional(),
  fields: z.array(fieldMetadataSchema).optional(),
});

export type Table = z.infer<typeof tableSchema>;

// ============================================================================
// Query Metadata Response
// ============================================================================

/**
 * Query metadata response schema
 * GET /api/card/:id/query_metadata
 */
export const queryMetadataSchema = z.object({
  databases: z.array(z.unknown()).optional(),
  tables: z.array(tableSchema).optional(),
});

export type QueryMetadata = z.infer<typeof queryMetadataSchema>;
